import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, TableWrap, Tabs } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, saveBlob } from '../lib/api.ts';
import { dateTime, day } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';

/**
 * Data protection: answering people who ask what the business holds about them or ask for it to
 * be erased (within 30 days), how long records about people are kept, and the register of
 * processing activities the law asks the business to keep.
 */

type Kind = 'access' | 'erasure';
interface Request {
  id: string;
  number: number;
  kind: Kind;
  state: 'open' | 'done' | 'refused';
  email: string | null;
  phone: string | null;
  person: string;
  received_on: string;
  due_on: string;
  closed_at: string | null;
  note: string | null;
  result: { erased: { model: string; label: string; count: number }[]; kept: { model: string; label: string; count: number; why: 'suppression' | 'money' | 'locked' }[] } | null;
}
interface DataKind {
  model: string;
  label: string;
  module: string;
  fields: string[];
  kept: 'suppression' | 'money' | null;
  records: number;
  archivable: boolean;
  retention: { days: number; active: boolean; last_run: string | null; last_count: number } | null;
}
interface Overview {
  today: string;
  answer_days: number;
  requests: Request[];
  open: number;
  late: number;
  kinds: DataKind[];
}
interface Found {
  kinds: { model: string; label: string; module: string; count: number; kept: 'suppression' | 'money' | null; fields: string[] }[];
  total: number;
}
interface Activity {
  id: string;
  name: string;
  purpose: string;
  basis: Basis;
  people: string;
  data: string;
  recipients: string | null;
  abroad: string | null;
  keep: string;
  security: string | null;
}
const BASES = ['consent', 'contract', 'legal_obligation', 'vital_interests', 'public_task', 'legitimate_interests'] as const;
type Basis = (typeof BASES)[number];
type Tab = 'requests' | 'data' | 'register';

const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
/** "customer.customer" → "Customer"; field names as people read them. */
const fieldName = (f: string) => f.replace(/_/g, ' ');

export function Privacy() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('privacy.overview');
  const tab = (['requests', 'data', 'register'].includes(params.get('tab') ?? '') ? params.get('tab') : 'requests') as Tab;
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === 'requests') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Request | null>(null);
  const kept = o?.kinds.filter((k) => k.retention?.active).length ?? 0;
  return (
    <Shell>
      <div className="page pv-page">
        <div className="page-head">
          <div>
            <h1>{t('pv.title')}</h1>
            <p className="muted">{t('pv.subtitle')}</p>
          </div>
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            {t('pv.new')}
          </Button>
        </div>
        {!o ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : (
          <>
            <dl className="fiscal-stats pv-stats">
              <div className="fiscal-stat">
                <dt>{t('pv.st_open')}</dt>
                <dd>{o.open}</dd>
              </div>
              <div className={`fiscal-stat${o.late ? ' danger' : ''}`}>
                <dt>{t('pv.st_late')}</dt>
                <dd>{o.late}</dd>
              </div>
              <div className="fiscal-stat">
                <dt>{t('pv.st_kinds')}</dt>
                <dd>{o.kinds.filter((k) => k.records).length}</dd>
              </div>
              <div className="fiscal-stat">
                <dt>{t('pv.st_rules')}</dt>
                <dd>{kept}</dd>
              </div>
            </dl>
            <Tabs<Tab>
              label={t('pv.sections')}
              value={tab}
              onChange={setTab}
              tabs={[
                { key: 'requests', label: t('pv.tab_requests'), icon: 'inbox', count: o.open },
                { key: 'data', label: t('pv.tab_data'), icon: 'shield' },
                { key: 'register', label: t('pv.tab_register'), icon: 'book' },
              ]}
            />
            {tab === 'requests' ? (
              <Requests o={o} onOpen={setOpen} onNew={() => setCreating(true)} />
            ) : tab === 'data' ? (
              <DataTab o={o} onChange={reload} />
            ) : (
              <Register />
            )}
          </>
        )}
      </div>
      {creating ? (
        <NewRequest
          days={o?.answer_days ?? 30}
          onClose={() => setCreating(false)}
          onDone={async (r) => {
            setCreating(false);
            await reload();
            setTab('requests');
            setOpen(r);
          }}
        />
      ) : null}
      {open ? (
        <RequestDialog
          request={open}
          today={o?.today ?? ''}
          onClose={() => setOpen(null)}
          onChange={async (r) => {
            await reload();
            setOpen(r);
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- requests -----------------------------------------------------------------------------

function StateBadge({ r, today }: { r: Request; today: string }) {
  if (r.state === 'done') return <StatusBadge tone="ok" icon="checkCircle" label={t('pv.s_done')} />;
  if (r.state === 'refused') return <StatusBadge tone="neutral" icon="x" label={t('pv.s_refused')} />;
  if (r.due_on < today) return <StatusBadge tone="danger" icon="alert" label={t('pv.s_late')} />;
  const left = daysBetween(today, r.due_on);
  return <StatusBadge tone={left <= 7 ? 'warn' : 'info'} icon="clock" label={tn('pv.s_days_left', left)} />;
}

const KIND_ICON: Record<Kind, IconName> = { access: 'download', erasure: 'trash' };

function Requests({ o, onOpen, onNew }: { o: Overview; onOpen: (r: Request) => void; onNew: () => void }) {
  if (!o.requests.length)
    return (
      <section className="panel">
        <EmptyState
          icon="shield"
          title={t('pv.no_requests')}
          body={t('pv.no_requests_body', { days: o.answer_days })}
          action={
            <Button icon="plus" onClick={onNew}>
              {t('pv.new')}
            </Button>
          }
        />
      </section>
    );
  return (
    <TableWrap label={t('pv.tab_requests')}>
      <Table
        view="privacy.requests"
        head={[t('pv.c_number'), t('pv.c_person'), t('pv.c_kind'), t('pv.c_received'), t('pv.c_due'), t('pv.c_state')]}
        left={[1, 2]}
        phoneHide={[0, 2, 3]}
        rows={o.requests.map((r) => ({
          key: r.id,
          onClick: () => onOpen(r),
          sort: [r.number, r.person, r.kind, r.received_on, r.due_on, r.state],
          cells: [
            <span className="tabular">#{r.number}</span>,
            <span className="pv-person">
              <strong>{r.person}</strong>
              <span className="muted small">{[r.email, r.phone].filter(Boolean).join(' · ')}</span>
            </span>,
            <span className="pv-kind">
              <Icon name={KIND_ICON[r.kind]} size={16} />
              {t(`pv.k_${r.kind}` as MessageKey)}
            </span>,
            day(r.received_on),
            day(r.due_on),
            <StateBadge r={r} today={o.today} />,
          ],
        }))}
      />
    </TableWrap>
  );
}

function NewRequest({ days, onClose, onDone }: { days: number; onClose: () => void; onDone: (r: Request) => void }) {
  const { busy, run } = useSave();
  const [kind, setKind] = useState<Kind>('access');
  const [person, setPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [received, setReceived] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const noContact = !email.trim() && !phone.trim();
  const save = async () => {
    setTried(true);
    if (person.trim().length < 2 || noContact) return;
    let made: Request | null = null;
    const ok = await run(async () => {
      made = await action<Request>('privacy.open_request', {
        kind,
        person: person.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        received_on: received || null,
        note: note.trim() || null,
      });
    }, t('pv.opened'));
    if (ok && made) onDone(made);
  };
  return (
    <Dialog
      title={t('pv.new')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
            {t('pv.open_request')}
          </Button>
        </>
      }
    >
      <div className="choice-cards pv-kinds" role="radiogroup" aria-label={t('pv.c_kind')}>
        {(['access', 'erasure'] as const).map((k) => (
          <button key={k} type="button" role="radio" aria-checked={kind === k} className={`choice-card${kind === k ? ' active' : ''}`} onClick={() => setKind(k)}>
            <span className="choice-card-title">
              <Icon name={KIND_ICON[k]} size={18} />
              {t(`pv.k_${k}` as MessageKey)}
            </span>
            <span className="muted small">{t(`pv.k_${k}_hint` as MessageKey)}</span>
          </button>
        ))}
      </div>
      <Field label={t('pv.f_person')} required error={tried && person.trim().length < 2 ? t('pv.e_person') : null}>
        <input className="input" value={person} onChange={(e) => setPerson(e.target.value)} autoComplete="off" data-autofocus />
      </Field>
      <div className="form-grid">
        <Field label={t('pv.f_email')} error={tried && noContact ? t('pv.e_contact') : null}>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
        </Field>
        <Field label={t('pv.f_phone')}>
          <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
        </Field>
      </div>
      <Field label={t('pv.f_received')} hint={t('pv.f_received_hint', { days })}>
        <input className="input" type="date" value={received} onChange={(e) => setReceived(e.target.value)} />
      </Field>
      <Field label={t('pv.f_note')} hint={t('pv.f_note_hint')}>
        <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}

const WHY: Record<'suppression' | 'money' | 'locked', MessageKey> = { suppression: 'pv.why_suppression', money: 'pv.why_money', locked: 'pv.why_locked' };

function RequestDialog({ request: r, today, onClose, onChange }: { request: Request; today: string; onClose: () => void; onChange: (r: Request) => Promise<void> }) {
  const { me } = useSession();
  const { busy, run } = useSave();
  const [found, setFound] = useState<Found | null>(null);
  const [closing, setClosing] = useState<'done' | 'refused' | null>(null);
  const [erasing, setErasing] = useState(false);
  const isOpen = r.state === 'open';
  const lookUp = () => run(async () => setFound(await action<Found>('privacy.look_up', { request: r.id })));
  const download = () =>
    run(async () => {
      const file = await action('privacy.export', { request: r.id });
      const name = `personal-data-${r.person.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase()}-${today}.json`;
      await saveBlob(new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }), name);
    }, t('pv.downloaded'));
  return (
    <Dialog
      title={t('pv.request_n', { n: r.number })}
      onClose={onClose}
      size="lg"
      footer={
        isOpen ? (
          <>
            <Button variant="ghost" icon="x" onClick={() => setClosing('refused')}>
              {t('pv.refuse')}
            </Button>
            {r.kind === 'erasure' ? (
              <Button variant="danger" icon="trash" onClick={() => setErasing(true)}>
                {t('pv.erase')}
              </Button>
            ) : (
              <Button variant="primary" icon="check" onClick={() => setClosing('done')}>
                {t('pv.mark_done')}
              </Button>
            )}
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        )
      }
    >
      <div className="pv-request">
        <div className="pv-request-head">
          <div>
            <h3>{r.person}</h3>
            <p className="muted small">{[r.email, r.phone].filter(Boolean).join(' · ') || t('pv.identity_erased')}</p>
          </div>
          <StateBadge r={r} today={today} />
        </div>
        <dl className="pv-facts">
          <div>
            <dt>{t('pv.c_kind')}</dt>
            <dd>
              <Icon name={KIND_ICON[r.kind]} size={16} /> {t(`pv.k_${r.kind}` as MessageKey)}
            </dd>
          </div>
          <div>
            <dt>{t('pv.c_received')}</dt>
            <dd>{day(r.received_on)}</dd>
          </div>
          <div>
            <dt>{t('pv.c_due')}</dt>
            <dd>{day(r.due_on)}</dd>
          </div>
          {r.closed_at ? (
            <div>
              <dt>{t('pv.c_closed')}</dt>
              <dd>{dateTime(r.closed_at)}</dd>
            </div>
          ) : null}
        </dl>
        {r.note ? <p className="pv-note">{r.note}</p> : null}

        {r.result ? (
          <section className="pv-result" aria-label={t('pv.result')}>
            <h4>{t('pv.result')}</h4>
            <ul>
              {r.result.erased.map((x) => (
                <li key={x.model}>
                  <Icon name="checkCircle" size={16} className="pv-ok" />
                  <span>{tn('pv.erased_n', x.count, { kind: x.label })}</span>
                </li>
              ))}
              {r.result.kept.map((x) => (
                <li key={x.model}>
                  <Icon name="lock" size={16} className="pv-kept" />
                  <span>
                    {tn('pv.kept_n', x.count, { kind: x.label })} <span className="muted">· {t(WHY[x.why])}</span>
                  </span>
                </li>
              ))}
              {!r.result.erased.length && !r.result.kept.length ? <li className="muted">{t('pv.nothing_held')}</li> : null}
            </ul>
          </section>
        ) : isOpen ? (
          <section className="pv-found" aria-live="polite">
            <div className="pv-found-head">
              <div>
                <h4>{t('pv.what_we_hold')}</h4>
                <p className="muted small">{t('pv.what_we_hold_hint')}</p>
              </div>
              <div className="pv-found-actions">
                <Button size="sm" variant="secondary" icon="search" busy={busy && !found} onClick={() => void lookUp()}>
                  {found ? t('pv.look_again') : t('pv.look_up')}
                </Button>
                {r.kind === 'access' ? (
                  <Button size="sm" variant="secondary" icon="download" onClick={() => void download()}>
                    {t('pv.download')}
                  </Button>
                ) : null}
              </div>
            </div>
            {found ? (
              found.kinds.length ? (
                <ul className="pv-found-list">
                  {found.kinds.map((k) => (
                    <li key={k.model}>
                      <span className="pv-found-kind">
                        <strong>{k.label}</strong>
                        <span className="muted small">{k.fields.map(fieldName).join(', ')}</span>
                      </span>
                      <span className="tabular">{tn('pv.records_n', k.count)}</span>
                      {k.kept ? <StatusBadge tone="neutral" icon="lock" label={t('pv.kept')} /> : <span />}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted pv-none">{t('pv.nothing_held')}</p>
              )
            ) : null}
          </section>
        ) : null}
      </div>
      {closing ? <CloseRequest request={r} state={closing} onClose={() => setClosing(null)} onDone={onChange} /> : null}
      {erasing ? <EraseDialog request={r} secondStep={!!me?.mfa} onClose={() => setErasing(false)} onDone={onChange} /> : null}
    </Dialog>
  );
}

function CloseRequest({ request: r, state, onClose, onDone }: { request: Request; state: 'done' | 'refused'; onClose: () => void; onDone: (r: Request) => Promise<void> }) {
  const { busy, run } = useSave();
  const [note, setNote] = useState(r.note ?? '');
  const [tried, setTried] = useState(false);
  const save = async () => {
    setTried(true);
    if (state === 'refused' && !note.trim()) return;
    let done: Request | null = null;
    const ok = await run(async () => {
      done = await action<Request>('privacy.close_request', { request: r.id, state, note: note.trim() || null });
    }, state === 'done' ? t('pv.closed_done') : t('pv.closed_refused'));
    if (ok && done) {
      onClose();
      await onDone(done);
    }
  };
  return (
    <Dialog
      title={state === 'done' ? t('pv.mark_done') : t('pv.refuse')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {state === 'done' ? t('pv.mark_done') : t('pv.refuse')}
          </Button>
        </>
      }
    >
      <p className="muted small">{state === 'done' ? t('pv.done_hint') : t('pv.refuse_hint')}</p>
      <Field label={state === 'done' ? t('pv.f_note') : t('pv.f_reason')} required={state === 'refused'} error={tried && state === 'refused' && !note.trim() ? t('pv.e_reason') : null}>
        <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

function EraseDialog({ request: r, secondStep, onClose, onDone }: { request: Request; secondStep: boolean; onClose: () => void; onDone: (r: Request) => Promise<void> }) {
  const { busy, run } = useSave();
  const [sure, setSure] = useState(false);
  const erase = async () => {
    let done: Request | null = null;
    const ok = await run(async () => {
      done = await action<Request>('privacy.erase', { request: r.id });
    }, t('pv.erased_ok'));
    if (ok && done) {
      onClose();
      await onDone(done);
    }
  };
  return (
    <Dialog
      title={t('pv.erase_title', { person: r.person })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="trash" busy={busy} disabled={!sure || !secondStep} onClick={() => void erase()}>
            {t('pv.erase')}
          </Button>
        </>
      }
    >
      <ul className="pv-erase-list">
        <li>
          <Icon name="trash" size={16} /> {t('pv.erase_what')}
        </li>
        <li>
          <Icon name="lock" size={16} /> {t('pv.erase_kept')}
        </li>
        <li>
          <Icon name="history" size={16} /> {t('pv.erase_proof')}
        </li>
      </ul>
      {!secondStep ? (
        <p className="callout callout-warn small" role="note">
          <Icon name="shield" size={16} /> {t('pv.needs_mfa')}
        </p>
      ) : null}
      <label className="check-row">
        <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} data-autofocus />
        <span>{t('pv.erase_sure')}</span>
      </label>
    </Dialog>
  );
}

// ---- where personal data is, and how long it is kept ----------------------------------------

const keepText = (k: DataKind) =>
  k.kept === 'suppression'
    ? t('pv.keep_suppression')
    : k.kept === 'money'
      ? t('pv.keep_money')
      : k.retention?.active
        ? k.retention.days % 365 === 0
          ? tn('pv.keep_years', k.retention.days / 365)
          : tn('pv.keep_days', k.retention.days)
        : t('pv.keep_forever');

function DataTab({ o, onChange }: { o: Overview; onChange: () => Promise<unknown> }) {
  const { me } = useSession();
  const { busy, run } = useSave();
  const [editing, setEditing] = useState<DataKind | null>(null);
  const [running, setRunning] = useState(false);
  const rules = o.kinds.filter((k) => k.retention?.active);
  const sorted = [...o.kinds].sort((a, b) => b.records - a.records || a.label.localeCompare(b.label));
  return (
    <div className="pv-data">
      <p className="callout callout-info" role="note">
        <Icon name="info" size={16} />
        <span>{t('pv.data_intro')}</span>
      </p>
      <section className="list-card">
        <div className="list-card-head">
          <div>
            <h2>{t('pv.where')}</h2>
            <p className="muted">{t('pv.where_hint')}</p>
          </div>
          <Button variant="secondary" size="sm" icon="trash" disabled={!rules.length} onClick={() => setRunning(true)}>
            {t('pv.run_now')}
          </Button>
        </div>
        <TableWrap>
          <Table
            view="privacy.data"
            head={[t('pv.c_records_kind'), t('pv.c_fields'), t('pv.c_records'), t('pv.c_keep'), '']}
            left={[1, 3]}
            phoneHide={[1]}
            rows={sorted.map((k) => ({
              key: k.model,
              sort: [k.label, k.fields.length, k.records, k.retention?.active ? k.retention.days : k.kept ? -1 : 1e9, 0],
              cells: [
                <strong>{k.label}</strong>,
                <span className="muted small pv-fields">{k.fields.map(fieldName).join(', ')}</span>,
                <span className="tabular">{k.records}</span>,
                <span className="pv-keep">
                  {keepText(k)}
                  {k.retention?.active && k.archivable ? <span className="muted small">{t('pv.archived_only')}</span> : null}
                  {k.retention?.last_run ? <span className="muted small">{t('pv.last_run', { when: dateTime(k.retention.last_run), n: k.retention.last_count })}</span> : null}
                </span>,
                k.kept ? <span /> : <IconButton icon="edit" label={t('pv.set_keep', { kind: k.label })} onClick={() => setEditing(k)} />,
              ],
            }))}
          />
        </TableWrap>
      </section>
      {editing ? (
        <RetentionDialog
          kind={editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await onChange();
          }}
        />
      ) : null}
      {running ? (
        <Confirm
          title={t('pv.run_now')}
          body={
            <>
              <p>{t('pv.run_body')}</p>
              {!me?.mfa ? (
                <p className="callout callout-warn small" role="note">
                  <Icon name="shield" size={16} /> {t('pv.needs_mfa')}
                </p>
              ) : null}
            </>
          }
          confirm={t('pv.run_confirm')}
          danger
          busy={busy}
          onClose={() => setRunning(false)}
          onConfirm={() =>
            void run(async () => {
              const r = await action<{ model: string; count: number }[]>('privacy.run_retention', {});
              setRunning(false);
              await onChange();
              return r;
            }, t('pv.run_done'))
          }
        />
      ) : null}
    </div>
  );
}

const PRESETS = [
  { days: 365, key: 'pv.p_1y' },
  { days: 730, key: 'pv.p_2y' },
  { days: 1095, key: 'pv.p_3y' },
  { days: 1825, key: 'pv.p_5y' },
  { days: 3650, key: 'pv.p_10y' },
] as const;

function RetentionDialog({ kind, onClose, onDone }: { kind: DataKind; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [days, setDays] = useState(String(kind.retention?.active ? kind.retention.days : 730));
  const n = Number(days);
  const bad = !Number.isInteger(n) || n < 30 || n > 36_500;
  const save = (value: number | null) =>
    run(
      async () => {
        await action('privacy.set_retention', { model: kind.model, days: value });
        await onDone();
      },
      value === null ? t('pv.keep_saved_forever') : t('pv.keep_saved'),
    );
  return (
    <Dialog
      title={t('pv.set_keep', { kind: kind.label })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {kind.retention?.active ? (
            <Button variant="ghost" onClick={() => void save(null)}>
              {t('pv.keep_forever_btn')}
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          )}
          <Button variant="primary" busy={busy} disabled={bad} onClick={() => void save(n)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <p className="muted small">{kind.archivable ? t('pv.keep_hint_archived') : t('pv.keep_hint')}</p>
      <div className="pv-presets" role="group" aria-label={t('pv.presets')}>
        {PRESETS.map((p) => (
          <button key={p.days} type="button" className={`chip${n === p.days ? ' active' : ''}`} aria-pressed={n === p.days} onClick={() => setDays(String(p.days))}>
            {t(p.key)}
          </button>
        ))}
      </div>
      <Field label={t('pv.f_days')} error={bad ? t('pv.e_days') : null}>
        <input className="input" type="number" inputMode="numeric" min={30} max={36500} value={days} onChange={(e) => setDays(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

// ---- the register of processing activities -------------------------------------------------

function Register() {
  const { data, reload } = useAction<Activity[]>('privacy.activities');
  const { busy, run } = useSave();
  const [editing, setEditing] = useState<Activity | 'new' | null>(null);
  const [removing, setRemoving] = useState<Activity | null>(null);
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  return (
    <div className="pv-register">
      <div className="pv-register-head">
        <p className="muted">{t('pv.register_intro')}</p>
        <div className="pv-register-actions">
          <Button variant="secondary" size="sm" icon="printer" onClick={() => window.print()} disabled={!data.length}>
            {t('pv.print')}
          </Button>
          <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing('new')}>
            {t('pv.add_activity')}
          </Button>
        </div>
      </div>
      <p className="callout callout-info pv-security" role="note">
        <Icon name="shieldCheck" size={16} />
        <span>
          <strong>{t('pv.security_title')}</strong> {t('pv.security_body')}
        </span>
      </p>
      {!data.length ? (
        <section className="panel">
          <EmptyState icon="book" title={t('pv.no_activities')} body={t('pv.no_activities_body')} />
        </section>
      ) : (
        <ol className="pv-activities">
          {data.map((a) => (
            <li key={a.id} className="panel pv-activity">
              <div className="pv-activity-head">
                <h3>{a.name}</h3>
                <span className="pv-basis">{t(`pv.b_${a.basis}` as MessageKey)}</span>
                <span className="pv-activity-actions">
                  <IconButton icon="edit" label={t('pv.edit_activity', { name: a.name })} onClick={() => setEditing(a)} />
                  <IconButton icon="trash" label={t('pv.remove_activity', { name: a.name })} onClick={() => setRemoving(a)} />
                </span>
              </div>
              <p>{a.purpose}</p>
              <dl className="pv-activity-facts">
                <div>
                  <dt>{t('pv.a_people')}</dt>
                  <dd>{a.people}</dd>
                </div>
                <div>
                  <dt>{t('pv.a_data')}</dt>
                  <dd>{a.data}</dd>
                </div>
                <div>
                  <dt>{t('pv.a_recipients')}</dt>
                  <dd>{a.recipients || t('pv.a_none')}</dd>
                </div>
                <div>
                  <dt>{t('pv.a_abroad')}</dt>
                  <dd>{a.abroad || t('pv.a_no_abroad')}</dd>
                </div>
                <div>
                  <dt>{t('pv.a_keep')}</dt>
                  <dd>{a.keep}</dd>
                </div>
                {a.security ? (
                  <div>
                    <dt>{t('pv.a_security')}</dt>
                    <dd>{a.security}</dd>
                  </div>
                ) : null}
              </dl>
            </li>
          ))}
        </ol>
      )}
      {editing ? (
        <ActivityDialog
          activity={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}
      {removing ? (
        <Confirm
          title={t('pv.remove_activity', { name: removing.name })}
          body={t('pv.remove_body')}
          confirm={t('common.delete')}
          danger
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(async () => {
              await action('privacy.remove_activity', { id: removing.id });
              setRemoving(null);
              await reload();
            }, t('pv.removed'))
          }
        />
      ) : null}
    </div>
  );
}

function ActivityDialog({ activity, onClose, onDone }: { activity: Activity | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: activity?.name ?? '',
    purpose: activity?.purpose ?? '',
    basis: (activity?.basis ?? 'contract') as Basis,
    people: activity?.people ?? '',
    data: activity?.data ?? '',
    recipients: activity?.recipients ?? '',
    abroad: activity?.abroad ?? '',
    keep: activity?.keep ?? '',
    security: activity?.security ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const missing = {
    name: f.name.trim().length < 2,
    purpose: f.purpose.trim().length < 5,
    people: f.people.trim().length < 2,
    data: f.data.trim().length < 2,
    keep: f.keep.trim().length < 2,
  };
  const save = async () => {
    setTried(true);
    if (Object.values(missing).some(Boolean)) return;
    const ok = await run(() => action('privacy.save_activity', { id: activity?.id ?? null, ...f }), t('pv.activity_saved'));
    if (ok) await onDone();
  };
  const err = (k: keyof typeof missing) => (tried && missing[k] ? t('pv.e_required') : null);
  return (
    <Dialog
      title={activity ? t('pv.edit_activity', { name: activity.name }) : t('pv.add_activity')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('pv.a_name')} required error={err('name')}>
          <input className="input" value={f.name} onChange={set('name')} data-autofocus />
        </Field>
        <Field label={t('pv.a_basis')} required hint={t(`pv.bh_${f.basis}` as MessageKey)}>
          <select className="input" value={f.basis} onChange={set('basis')}>
            {BASES.map((b) => (
              <option key={b} value={b}>
                {t(`pv.b_${b}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={t('pv.a_purpose')} required error={err('purpose')}>
        <textarea className="input" rows={2} value={f.purpose} onChange={set('purpose')} />
      </Field>
      <div className="form-grid">
        <Field label={t('pv.a_people')} required error={err('people')} hint={t('pv.a_people_hint')}>
          <input className="input" value={f.people} onChange={set('people')} />
        </Field>
        <Field label={t('pv.a_keep')} required error={err('keep')} hint={t('pv.a_keep_hint')}>
          <input className="input" value={f.keep} onChange={set('keep')} />
        </Field>
      </div>
      <Field label={t('pv.a_data')} required error={err('data')}>
        <textarea className="input" rows={2} value={f.data} onChange={set('data')} />
      </Field>
      <div className="form-grid">
        <Field label={t('pv.a_recipients')} hint={t('pv.a_recipients_hint')}>
          <textarea className="input" rows={2} value={f.recipients} onChange={set('recipients')} />
        </Field>
        <Field label={t('pv.a_abroad')} hint={t('pv.a_abroad_hint')}>
          <textarea className="input" rows={2} value={f.abroad} onChange={set('abroad')} />
        </Field>
      </div>
      <Field label={t('pv.a_security')}>
        <textarea className="input" rows={2} value={f.security} onChange={set('security')} />
      </Field>
    </Dialog>
  );
}
